import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Form, Input, Row, Select, SelectProps } from "antd";
import { useParams } from "react-router-dom";
import { Toast } from 'primereact/toast';
import HTTPService from "../../../utils/makerequest";
import { ICompanyData } from "../../../interfaces/ICompanyData";
import { IAuthentication, UserRole } from "../../../interfaces/authentication";
import { StateLoader } from "../../../redux/StateLoader";
import { IBrandData } from "../../../interfaces/IBrandData";
import { useTranslation } from 'react-i18next';
import { ICampusData } from "../../../interfaces/ICampusData";
import { IPageProps } from "../../../interfaces/page-data";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateCampus: React.FunctionComponent<IPageProps> = () => {
  const { campusId } = useParams<any>();
  const toast = useRef<any>(null);
  const [campusData, setCampusData] = useState<ICampusData>();
  const [companyData, setCompanyData] = useState<ICompanyData[]>();
  const loader = new StateLoader();
  const userInfo: IAuthentication = loader.loadAuthState();
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
    if (campusId) {
      getCampusData(campusId);
    }
  }, [campusId]);

  const fetchData = async () => {
    const result = await HTTPService.GETCompanyNames();
    setCompanyData(result);
  };

  const getCampusData = (campusId: number) => {
    HTTPService.GET(`/Campus/ById?campusId=${campusId}`)
      .then((res) => {
        if (res.status === 200 && res.data) {
          setCampusData(res.data);
        } else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: res.error?.message || t('error_fetching_data'),
            life: 3000
          });
        }
      })
      .catch((error) => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('error_fetching_data'),
          life: 3000
        });
        console.error("Error fetching brand data:", error);
      });
  };

  const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
    return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
  };


  const updateCampus = (formData) => {
    const updatedCampus = {
      campusId: campusId,
      campusName: formData.campusName,
      barcode: formData.barcode,
      companyId: formData.companyId || userInfo.companyId,
    };

    HTTPService.PUT(`/Campus/updateCampus`, updatedCampus)
      .then((res) => {
        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary: t('successful'),
            detail: t('campus_updated_successfully'),
            life: 3000
          });
          window.location.replace("/admin/campus-management");
        }
        else if (res.status === 450) {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        } else if (res.status === 400) {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t(res.data),
            life: 3000
          });
        }
        else {
          toast.current?.show({
            severity: 'error',
            summary: t('mistake'),
            detail: t('campus_update_failed'),
            life: 3000
          });
        }
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: t('mistake'),
          detail: t('campus_update_failed'),
          life: 3000
        });
      });
  };

  const handleCancel = () => {
    window.location.replace("/admin/campus-management/");
  };

  return (
    <>
      <Toast ref={toast} />
      <Form
        layout="vertical"
        onFinish={updateCampus}
        fields={[
          { name: ["campusName"], value: campusData?.campusName },
          { name: ["barcode"], value: campusData?.barcode },
          { name: ["companyId"], value: campusData?.companyId },
        ]}
      >
        <Card style={{ padding: "50px" }}>
          <h3>{t('update_campus')}</h3>
          {userInfo?.role === UserRole.AltisAdmin ? (
            <Form.Item
              name='companyId'
              label={t('Select company')}
              rules={[
                {
                  required: true,
                  message: t('please_select_a_company'),
                },
              ]}
            >
              <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '6px' }} filterOption={filterOption}>
                {companyData?.map((company) => (
                  <Select.Option key={company.companyId} value={company.companyId}>
                    {company.companyName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          ) : null}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="campusName"
                label={t("campus")}
                rules={[
                  {
                    required: true,
                    message: t('please_input_your_campus'),
                    whitespace: true,
                  },
                ]}
              >
                <Input style={{ borderRadius: 0 }} placeholder={t('campus')} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name='barcode'
            label={t('barcode')}
          >
            <Input
              placeholder={t('barcode')}
              style={{ borderRadius: '0' }}
              value={campusData?.barcode}
              readOnly
            />
          </Form.Item>

          <Form.Item>
            <Row gutter={[4, 4]} justify="end" align="middle">
                <Col xs={12} sm={4}>
                    <Button
                        block
                        onClick={handleCancel}
                        type="primary"
                        style={{
                            height: 64,
                            backgroundColor: 'transparent',
                            color: '#1890ff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            border: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            e.currentTarget.style.color = '#40a9ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.color = '#1890ff';
                        }}
                    >
                        <CloseOutlined style={{ fontSize: 24 }} />
                        <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                            {t('cancel')}
                        </span>
                    </Button>
                </Col>
                <Col xs={12} sm={4}>
                    <Button
                        block
                        htmlType="submit"
                        type="primary"
                        style={{
                            height: 64,
                            backgroundColor: 'transparent',
                            color: '#1890ff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 10px',
                            border: 'none',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                            e.currentTarget.style.color = '#40a9ff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.color = '#1890ff';
                        }}
                    >
                        <SyncOutlined style={{ fontSize: 24 }} />
                        <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                            {t('update')}
                        </span>
                    </Button>
                </Col>
            </Row>
          </Form.Item>
        </Card>
      </Form>
    </>
  );
};

export default UpdateCampus
